import type { ActiveTab } from "@/appInterface/windows/VoteWindow/types";
import type { UITimerData } from "@/interface/HeaderBar/RightBar/Timer/types";
import type { ContextMenuFlags, HeaderButtonNames } from "@/interface/types";
import type { MultiUser } from "@/multiManager";
import type { WidgetOptions } from "@/shared/types/widgetOptions";
import type { Tool } from "@/tools/types";
import type { widgetOption } from "@/widgetMenu/widgetMenu";

export type EventMap = {
    "canvas.zoom.update": { zoom: number };
    "search.results.update": { total: number; currentIndex: number };
    // TODO: board info type
    "board.info": Record<string, any>;
    "multi.cursors": { disableMultiCursors: boolean };
    "header.visibility": { name: string | null };
    "button.disabled": Record<string, boolean>;
    "multi.users.sessions": { users: MultiUser[] };
    "multi.users.trackingSession": { session: string | null };
    "vote.timer.visibility": { isVisible: boolean };
    "header.button.active.name": { name: HeaderButtonNames | null };
    "export.menu.open": undefined;
    "vote.start.visibility": { isVisible: boolean };
    "vote.start.activeTab": { tab: ActiveTab };
    "vote.active.visibility": { isVisible: boolean };
    "menu.widget.coordinates": {
        coordinates: {
            top: number;
            left: number;
            canvasMatrixKey: string;
            selectionKey: string;
        } | null;
    };
    "menu.widget.availableOptions": { options: WidgetOptions[] };
    "menu.widget.options": { options: Map<WidgetOptions, widgetOption[]> };
    "contextMenu.visibility": { isVisible: boolean; anchor?: HTMLButtonElement };
    "contextMenu.visibilityMenuItems": {
        menuItems: ContextMenuFlags;
    };
    "toolbox.currentTool": { tool: Tool };
    "toolBox.visibility": { isVisible: boolean };
    "moreTool.visibility": { isVisible: boolean };
    "stickerToolMenu.visibility": { isVisible: boolean };
    "stickerToolMenu.color": { color: string };
    "arrowToolMenu.visibility": { isVisible: boolean };
    "arrowToolMenu.type": { lineType: string };
    "emoteToolMenu.visibility": { isVisible: boolean };
    "emoteToolMenu.active": { activeEmote: string };
    "mindMapToolMenu.visibility": { isVisible: boolean };
    "mindMapToolMenu.activePreset": { preset: string };
    "penToolMenu.visibility": { isVisible: boolean };
    "penToolMenu.penType": { penType: string };
    "penToolMenu.color": { color: string };
    "penToolMenu.lineWidth": { lineWidth: number };
    "templateToolMenu.visibility": { isVisible: boolean };
    "timer.update": UITimerData;
    "minimap.update": { needUpdate: boolean };
    "paste.done": undefined;
    "presentation.mode.visibility": { isVisible: boolean };
    "presentation.mode.presentationUID": { UID: string };
    "link.input.coordinates": { coordinates: { top: number; left: number } | null };
    "link.input.defaultValue": { value: string };
    "link.popup.state": {
        state: { url: string; top: number; left: number } | null;
    };
};

export type EventMapKey = keyof EventMap;
export type EventData<T extends EventMapKey> = EventMap[T];
export type EventHandler<T extends EventMapKey> = (data: EventData<T>) => void;

class EventEmitter {
    private handlers: {
        [K in EventMapKey]?: Set<EventHandler<K>>;
    } = {};

    on<T extends EventMapKey>(event: T, handler: EventHandler<T>): void {
        if (!this.handlers[event]) {
            this.handlers[event] = new Set<EventHandler<EventMapKey>>();
        }
        this.handlers[event].add(handler);
    }

    off<T extends EventMapKey>(event: T, handler: EventHandler<T>): void {
        const eventHandlers = this.handlers[event] as Set<EventHandler<T>>;
        if (eventHandlers) {
            eventHandlers.delete(handler);
        }
    }

    dispatch<T extends EventMapKey>(event: T, data: EventData<T>): void {
        const eventHandlers = this.handlers[event] as Set<EventHandler<T>>;
        //todo удалить после переноса на реакт
        if (localStorage.getItem("debug")) {
            console.log(`[${event}]: ${eventHandlers?.size || 0} handlers`);
        }
        if (eventHandlers) {
            eventHandlers.forEach((handler) => {
                try {
                    handler(data);
                } catch (error) {
                    console.error(`Error in event handler for ${event}:`, error);
                }
            });
        }
    }
}

export default new EventEmitter();
